import _ from 'lodash';
import { removeMissingAbbreviations } from 'modules/Abbreviations/processes';
import { selectAbbreviationsData } from 'modules/Abbreviations/store/selectors';
import { call, put, select } from 'redux-saga/effects';
import { startHandlingReusableLayoutsEditing, stopHandlingReusableLayoutsEditing } from 'containers/App/actions';
import * as commonSelectors from 'containers/Common/selectors';
import { getProjectAssets } from 'containers/Common/services/getProjectAssets';
import { initApp } from 'containers/Common/services/initApp';
import { documents as documentsSelector } from 'containers/Documents/selectors';
import * as Models from 'models';
import { refreshLayoutsOnArtboards } from 'services/processes';
import { toJS } from 'utils/immutable';
import { isGroupLayoutAssets } from 'utils/isGroupLayoutAssets';
import { isGroupLayout } from 'utils/layouts/isGroupLayout';
import { isPlainLayout } from 'utils/layouts/isPlainLayout';

export function* restoreLayoutAssets(
  layoutToRestore: Models.CombinedLayoutMap,
  editedLayout: Models.CombinedLayoutMap,
  prevLayoutAssetsByLayoutId: Models.ExtendedCombinedLayoutAssetsByLayoutId,
  currentLayoutAssetsByLayoutId: Models.ExtendedCombinedLayoutAssetsByLayoutId,
) {
  try {
    if (!layoutToRestore) {
      return;
    }

    const prevLayoutAssets = prevLayoutAssetsByLayoutId[layoutToRestore.get('id')];
    const currentLayoutAssets = currentLayoutAssetsByLayoutId[layoutToRestore.get('id')];

    if (!prevLayoutAssets) {
      return;
    }

    const abbreviationSelectData = yield select(selectAbbreviationsData);
    const textAbbreviationDocuments =
      (abbreviationSelectData as unknown as Models.TextAbbreviationsMap).toJS() as Models.TextAbbreviationDocumentsArray;

    yield put(stopHandlingReusableLayoutsEditing());

    const projectAssets: ReturnTypeSaga<typeof getProjectAssets> = yield call(getProjectAssets, { removeInternalInfo: false });
    const masterScreenData = (yield select(commonSelectors.masterScreenData)).toJS() as Models.MasterScreenData;
    const assets = _.set<Models.ProjectAssets>(_.cloneDeep(projectAssets), 'masterScreenData', masterScreenData);
    const arg = { assets } as Models.RestoreLayoutAssetsArgs;

    if (isGroupLayoutAssets(prevLayoutAssets) && isGroupLayoutAssets(currentLayoutAssets)) {
      const { usedDocuments } = toJS(prevLayoutAssets);

      _.update(assets, 'documents', documents => ({ ...usedDocuments, ...documents }));
    }

    const { artboards, layouts } = assets;
    const layoutToRestoreDocumentId: string = layoutToRestore.get('documentId');
    const artboardChildLayouts = _.pick(layouts, _.flatMap(artboards, artboard => artboard.layoutIds));
    const childLayoutsByGroupLayoutId = _(layouts)
      .filter<Models.LayeredGroupLayout>(isGroupLayout)
      .map<[string, Models.LayeredLayout[]]>(layout => [layout.id, layout.layoutIds.map(id => layouts[id] as Models.LayeredLayout)])
      .fromPairs()
      .value();

    const artboardChildLayoutsToRefresh = _.pickBy(artboardChildLayouts, ({ documentId }) => _.includes(documentId, layoutToRestoreDocumentId));

    // if layout is used within artboards and GRLs, all the GRLs were it's used should be refreshed
    const groupLayoutsToRefresh = isPlainLayout(layoutToRestore)
      ?
      _(childLayoutsByGroupLayoutId)
        .pickBy(childLayouts => childLayouts.some(({ documentId }) => _.includes(documentId, layoutToRestoreDocumentId)))
        .mapValues((_, groupLayoutId) => layouts[groupLayoutId])
        .value()
      :
      {};

    arg.layoutsToRefresh = { ...artboardChildLayoutsToRefresh, ...groupLayoutsToRefresh };

    if (isPlainLayout(layoutToRestore)) {
      const restoredLayout = arg.assets.documents[layoutToRestoreDocumentId] as Models.ReusableLayout;

      restoredLayout.documents = toJS(prevLayoutAssets.get('documents'));
      restoredLayout.entities.relationId = toJS(prevLayoutAssets.get('relationId'));
      restoredLayout.entities.relations = toJS(prevLayoutAssets.get('relations'));
      restoredLayout.entities.styles = toJS(prevLayoutAssets.get('styles'));
      restoredLayout.entities.brandDefinition = toJS(prevLayoutAssets.get('brandDefinition'));
      restoredLayout.entities.layoutSourceBrandDefinition = toJS(prevLayoutAssets.get('layoutSourceBrandDefinition'));
      restoredLayout.entities.accessibilityRole = toJS(prevLayoutAssets.get('accessibilityRole'));
    }

    yield call(refreshLayoutsOnArtboards, arg, false);
    const allDocuments = (yield select(documentsSelector)).toJS() as Models.CombinedDocuments;
    // combine documents after refresh because documents that are being uploaded at the moment are not involved into refresh
    arg.assets.documents = removeMissingAbbreviations(
      { ...allDocuments, ...arg.assets.documents },
      textAbbreviationDocuments,
    );

    yield call(initApp, { projectAssets: arg.assets });
  } finally {
    yield put(startHandlingReusableLayoutsEditing());
  }
}
